import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Save, Loader2, ArrowLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { toast } from 'sonner';

const rules = [
  { test: (p) => p.length >= 8, label: 'Mínimo 8 caracteres' },
  { test: (p) => /\d/.test(p), label: 'Pelo menos 1 número' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'Pelo menos 1 caractere especial' }
];

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const allPassed = rules.every((r) => r.test(form.new_password)) && form.new_password === form.confirm_password && form.new_password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPassed) return;
    setSaving(true);
    try {
      await api.changePassword(form.current_password, form.new_password, form.confirm_password);
      toast.success('Senha alterada com sucesso!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 py-12 px-4" data-testid="change-password-page">
      <div className="max-w-lg mx-auto">
        <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)} data-testid="change-pw-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Alterar senha</CardTitle>
            <CardDescription>Use uma senha forte. Todas as sessões atuais continuarão ativas.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
              <div>
                <Label htmlFor="current_password">Senha atual</Label>
                <PasswordInput
                  id="current_password"
                  leftIcon={Lock}
                  autoComplete="current-password"
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label htmlFor="new_password">Nova senha</Label>
                <PasswordInput
                  id="new_password"
                  leftIcon={Lock}
                  autoComplete="new-password"
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirmar nova senha</Label>
                <PasswordInput
                  id="confirm_password"
                  leftIcon={Lock}
                  autoComplete="new-password"
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <ul className="text-sm space-y-1 mt-3">
                {rules.map((r, i) => {
                  const ok = r.test(form.new_password);
                  return (
                    <li key={i} className={ok ? 'text-emerald-600' : 'text-slate-400'}>
                      {ok ? '✓' : '○'} {r.label}
                    </li>
                  );
                })}
                {form.new_password && form.confirm_password && (
                  <li className={form.new_password === form.confirm_password ? 'text-emerald-600' : 'text-red-500'}>
                    {form.new_password === form.confirm_password ? '✓' : '✗'} Senhas coincidem
                  </li>
                )}
              </ul>

              <Button
                type="submit"
                disabled={!allPassed || saving}
                className="w-full bg-gradient-to-r from-amber-500 to-red-500 text-white"
                data-testid="change-password-submit"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…</> : <><Save className="w-4 h-4 mr-2" /> Atualizar senha</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
